import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";

import { fmtDate, fmtCoords, toNum } from "../../utils/format";
import { padlockStatusClass, padlockStatusLabel } from "../../utils/padlock";
import { resolveCompanyFromLocalOrApi } from "../../services/auth";

function StatusPill({ status }) {
  const cls = padlockStatusClass(status);
  const label = padlockStatusLabel(status);
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
}

export default function LocksSupervisor() {
  const navigate = useNavigate();

  const [company, setCompany] = useState({ id: null, name: "—", resolving: true, error: null });
  const [padlocks, setPadlocks] = useState([]); // /padlock (todos)
  const [latest, setLatest] = useState([]);     // /padlock/latest-locations (todos)
  const [rows, setRows] = useState([]);         // filas normalizadas y filtradas
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // 1) Resolver empresa del usuario (localStorage -> fallback /users/:id)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await resolveCompanyFromLocalOrApi();
        if (!alive) return;
        setCompany({ id: c.id, name: c.name || "—", resolving: false, error: null });
      } catch (e) {
        if (!alive) return;
        setCompany({ id: null, name: "—", resolving: false, error: String(e) });
      }
    })();
    return () => { alive = false; };
  }, []);

  // 2) Cargar /padlock y /padlock/latest-locations (crudos, una vez)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [listRes, latestRes] = await Promise.all([
          api.get(endpoints.padlock.list),
          api.get(endpoints.padlock.latestLocations),
        ]);
        const allPadlocks = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data ?? []);
        const allLatest = Array.isArray(latestRes.data) ? latestRes.data : (latestRes.data?.data ?? []);
        if (!alive) return;
        setPadlocks(allPadlocks);
        setLatest(allLatest);
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // 3) Construir tabla filtrada por empresa y enriquecida con latest
  useEffect(() => {
    if (company.resolving) return;

    if (company.id === null) {
      setRows([]);
      return;
    }

    // Mapa latest por padlockId (lat/lng ya triangulados desde backend)
    const latestById = new Map();
    for (const it of latest) {
      const pid = toNum(it?.id ?? it?.padlockId ?? it?.padlock?.id ?? it?.padlock_id);
      if (pid === null) continue;

      const lat = toNum(it?.lat);
      const lng = toNum(it?.lng);
      const updated = it?.updated ?? it?.lastSyncAt ?? it?.eventAt ?? null;

      latestById.set(pid, { lat, lng, updated });
    }

    // Filtrar padlocks a los de la empresa
    const filtered = padlocks.filter((p) => {
      const pid = toNum(p?.company?.id ?? p?.companyId ?? p?.company_id);
      return pid === company.id;
    });

    // Normalizar filas
    const out = filtered.map((p) => {
      const id = toNum(p?.id);
      const name = p?.name || "—";
      const serial = p?.serialNumber || "—";
      const status = String(p?.status || "UNKNOWN").toUpperCase();
      const lastRow = id !== null ? latestById.get(id) : null;

      const lastUpdatedAt =
        (lastRow && lastRow.updated) ||
        p?.updatedAt ||
        p?.updated ||
        p?.updated_at ||
        null;

      const locationLabel =
        lastRow && lastRow.lat != null && lastRow.lng != null
          ? fmtCoords(lastRow.lat, lastRow.lng)
          : "—";

      return {
        id,
        name,
        serialNumber: serial,
        status,
        lastUpdatedAt,
        locationLabel,
      };
    });

    setRows(out);
  }, [company, padlocks, latest]);

  const headerCompanyName = company.name;

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Candados</h1>
        <p className="text-gray-600">
          Empresa: <span className="font-medium">{headerCompanyName}</span>
        </p>
      </div>

      {!company.resolving && company.id === null && (
        <div className="p-3 rounded bg-yellow-50 text-yellow-800 border border-yellow-200 mb-4">
          Tu usuario no tiene empresa asignada. No se muestran dispositivos.
        </div>
      )}

      <div className="bg-white border rounded shadow overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-3">Nombre</th>
              <th className="p-3">Serial</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Última actualización</th>
              <th className="p-3">Ubicación</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="p-4 text-gray-500">Cargando…</td>
              </tr>
            )}

            {err && !loading && (
              <tr>
                <td colSpan={6} className="p-4 text-red-600">
                  Error al cargar: {String(err)}
                </td>
              </tr>
            )}

            {!loading && !err && rows.length === 0 && company.id !== null && (
              <tr>
                <td colSpan={6} className="p-4 text-gray-500">
                  No hay candados para esta empresa.
                </td>
              </tr>
            )}

            {!loading && !err && rows.map((lock) => (
              <tr key={lock.id ?? lock.serialNumber} className="border-t hover:bg-gray-50">
                <td className="p-3">{lock.name}</td>
                <td className="p-3">{lock.serialNumber}</td>
                <td className="p-3">
                  <StatusPill status={lock.status} />
                </td>
                <td className="p-3">{fmtDate(lock.lastUpdatedAt)}</td>
                <td className="p-3">{lock.locationLabel}</td>
                <td className="p-3">
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        if (!lock.serialNumber) return;
                        navigate(`/padlocks/${encodeURIComponent(lock.serialNumber)}`);
                      }}
                      disabled={!lock.serialNumber}
                      className={`px-3 py-1 rounded border text-blue-600 hover:bg-blue-50 ${
                        !lock.serialNumber ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      Ver
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}